import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
  Box,
  Container,
  Card,
  CardContent,
  Typography,
  Grid,
  Chip,
  Button,
  Divider,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
  TextField
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  Close as CloseIcon
} from '@mui/icons-material';
import { getAssessmentById, getTrustScore, getControlResponses, sendInvitation, resendInvitation, executiveApproveAssessment, executiveRejectAssessment, getExecutiveApprovalHistory } from 'services/vendor-assessment-service';
import { TrustScoreDetail } from './TrustScoreDetail';
import { GapAnalysisView } from './GapAnalysisView';
import { ExecutiveApprovalModal } from './ExecutiveApprovalModal';
import { AssessmentTimeline } from './AssessmentTimeline';
import { formatDateTimeToDateTimeBR } from 'utils/date_utils';
import withReactContent from 'sweetalert2-react-content';
import Swal from 'sweetalert2';

/**
 * AssessmentDetail Component
 * Displays comprehensive assessment information with trust score and gap analysis
 */
export const AssessmentDetail = () => {
  const { t } = useTranslation(['gap-analysis', 'common']);
  const { id } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const SwalReact = withReactContent(Swal);
  const [showTrustScore, setShowTrustScore] = useState(false);
  const [showGapAnalysis, setShowGapAnalysis] = useState(false);
  const [showExecutiveApproval, setShowExecutiveApproval] = useState(false);
  const [executiveJustification, setExecutiveJustification] = useState('');

  // Fetch assessment data
  const { data: assessmentData, isLoading: assessmentLoading, error: assessmentError } = useQuery(
    ['assessment', id],
    () => getAssessmentById(id),
    {
      enabled: !!id,
      retry: false
    }
  );

  // Fetch trust score if assessment is submitted
  const { data: trustScoreData, isLoading: trustScoreLoading } = useQuery(
    ['trust-score', id],
    () => getTrustScore(id),
    {
      enabled: !!id && assessmentData?.data?.success &&
               ['Under Review', 'Pending Executive Approval', 'Approved', 'Rejected'].includes(assessmentData?.data?.data?.status),
      retry: false
    }
  );

  // Fetch control responses for gap analysis
  const { data: controlResponsesData } = useQuery(
    ['control-responses', id],
    () => getControlResponses(id),
    {
      enabled: !!id && assessmentData?.data?.success &&
               ['Under Review', 'Pending Executive Approval', 'Approved', 'Rejected'].includes(assessmentData?.data?.data?.status),
      retry: false
    }
  );

  // Fetch executive approval history
  const { data: approvalHistoryData } = useQuery(
    ['executive-approval-history', id],
    () => getExecutiveApprovalHistory(id),
    {
      enabled: !!id && assessmentData?.data?.success &&
               assessmentData?.data?.data?.status === 'Pending Executive Approval',
      retry: false
    }
  );

  const assessment = assessmentData?.data?.success ? assessmentData.data.data : null;
  const trustScore = trustScoreData?.data?.success ? trustScoreData.data.data : null;
  const controlResponses = controlResponsesData?.data?.success ? controlResponsesData.data.data : [];
  const approvalHistory = approvalHistoryData?.data?.success ? approvalHistoryData.data.data : null;

  // Send invitation mutation
  const sendMutation = useMutation(
    () => sendInvitation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', id]);
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.detail.swal.invitationSentTitle'),
          html: `<p>${t('gap-analysis:assessment.detail.swal.invitationSentHtml')}</p>`,
          timer: 3000
        });
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.detail.swal.errorTitle'),
          text: error.response?.data?.error || t('gap-analysis:assessment.detail.swal.errorSend')
        });
      }
    }
  );

  // Resend invitation mutation
  const resendMutation = useMutation(
    () => resendInvitation(id),
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', id]);
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.detail.swal.invitationResentTitle'),
          text: t('gap-analysis:assessment.detail.swal.invitationResentText'),
          timer: 2000
        });
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.detail.swal.errorTitle'),
          text: error.response?.data?.error || t('gap-analysis:assessment.detail.swal.errorResend')
        });
      }
    }
  );

  // Executive approve mutation
  const approveMutation = useMutation(
    async () => {
      const response = await executiveApproveAssessment(id, executiveJustification);
      if (!response.data?.success) {
        throw new Error(response.data?.error || 'Failed to approve assessment');
      }
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', id]);
        queryClient.invalidateQueries(['executive-approval-history', id]);
        setExecutiveJustification('');
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.detail.swal.approvedTitle'),
          text: t('gap-analysis:assessment.detail.swal.approvedText'),
          timer: 3000
        });
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.detail.swal.errorTitle'),
          text: error.message || error.response?.data?.error || t('gap-analysis:assessment.detail.swal.errorApprove')
        });
      }
    }
  );

  // Executive reject mutation
  const rejectMutation = useMutation(
    async () => {
      const response = await executiveRejectAssessment(id, executiveJustification);
      if (!response.data?.success) {
        throw new Error(response.data?.error || t('gap-analysis:assessment.detail.swal.errorReject'));
      }
      return response.data;
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['assessment', id]);
        queryClient.invalidateQueries(['executive-approval-history', id]);
        setExecutiveJustification('');
        SwalReact.fire({
          icon: 'success',
          title: t('gap-analysis:assessment.detail.swal.rejectedTitle'),
          text: t('gap-analysis:assessment.detail.swal.rejectedText'),
          timer: 3000
        });
      },
      onError: (error) => {
        SwalReact.fire({
          icon: 'error',
          title: t('gap-analysis:assessment.detail.swal.errorTitle'),
          text: error.message || error.response?.data?.error || t('gap-analysis:assessment.detail.swal.errorReject')
        });
      }
    }
  );

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'default',
      'Pending Vendor Response': 'warning',
      'Under Review': 'primary',
      'Pending Executive Approval': 'warning',
      'Approved': 'success',
      'Rejected': 'error',
      'Contractual Review': 'secondary'
    };
    return colors[status] || 'default';
  };

  if (assessmentLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (assessmentError || !assessment) {
    return (
      <Container maxWidth="lg">
        <Box py={4}>
          <Alert severity="error">
            {t('gap-analysis:assessment.detail.notFound')}
          </Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/vendor-assessments')}
            sx={{ mt: 2 }}
          >
            {t('gap-analysis:assessment.detail.backButton')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg">
      <Box py={4}>
        {/* Header */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={() => navigate('/vendor-assessments')}
            >
              {t('gap-analysis:assessment.detail.backButton')}
            </Button>
            <Typography variant="h4">
              {t('gap-analysis:assessment.detail.title')}
            </Typography>
          </Box>

          <Box display="flex" gap={2}>
            {assessment.status === 'Draft' && (
              <Button
                variant="contained"
                color="primary"
                startIcon={<EmailIcon />}
                onClick={() => sendMutation.mutate()}
                disabled={sendMutation.isLoading}
              >
                {sendMutation.isLoading ? t('gap-analysis:assessment.detail.sending') : t('gap-analysis:assessment.detail.sendInvitation')}
              </Button>
            )}
            {assessment.status === 'Pending Vendor Response' && (
              <Button
                variant="outlined"
                startIcon={<EmailIcon />}
                onClick={() => resendMutation.mutate()}
                disabled={resendMutation.isLoading}
              >
                {resendMutation.isLoading ? t('gap-analysis:assessment.detail.sending') : t('gap-analysis:assessment.detail.resendInvitation')}
              </Button>
            )}
          </Box>
        </Box>

        {/* Status-based alerts */}
        {assessment.status === 'Draft' && (
          <Alert severity="info" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('gap-analysis:assessment.detail.alerts.draft')}
            </Typography>
          </Alert>
        )}
        {assessment.status === 'Pending Vendor Response' && (
          <Alert severity="warning" sx={{ mb: 3 }}>
            <Typography variant="body2">
              {t('gap-analysis:assessment.detail.alerts.pendingVendorResponse')}
            </Typography>
          </Alert>
        )}

        <Grid container spacing={3}>
          {/* Assessment Information */}
          <Grid item xs={12} md={8}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('gap-analysis:assessment.detail.sections.assessmentInfo')}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                <Grid container spacing={2}>
                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      {t('gap-analysis:assessment.detail.fields.vendor')}
                    </Typography>
                    <Typography variant="body1" fontWeight="medium">
                      {assessment.vendorName}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      {t('gap-analysis:assessment.detail.fields.framework')}
                    </Typography>
                    <Typography variant="body1">
                      {assessment.frameworkName}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      {t('common:labels.status')}
                    </Typography>
                    <Box mt={0.5}>
                      <Chip
                        label={assessment.status}
                        color={getStatusColor(assessment.status)}
                        size="small"
                      />
                    </Box>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      {t('gap-analysis:assessment.detail.fields.targetMaturity')}
                    </Typography>
                    <Typography variant="body1">
                      {t('gap-analysis:assessment.detail.fields.level', { level: assessment.targetMaturityLevel })}
                    </Typography>
                  </Grid>

                  <Grid item xs={6}>
                    <Typography variant="caption" color="textSecondary">
                      {t('gap-analysis:assessment.detail.fields.createdDate')}
                    </Typography>
                    <Typography variant="body1">
                      {formatDateTimeToDateTimeBR(assessment.createdAt)}
                    </Typography>
                  </Grid>

                  {assessment.submittedAt && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('gap-analysis:assessment.detail.fields.submittedDate')}
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTimeToDateTimeBR(assessment.submittedAt)}
                      </Typography>
                    </Grid>
                  )}

                  {assessment.emailSentAt && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('gap-analysis:assessment.detail.fields.emailSent')}
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTimeToDateTimeBR(assessment.emailSentAt)}
                      </Typography>
                    </Grid>
                  )}

                  {assessment.tokenExpiration && (
                    <Grid item xs={6}>
                      <Typography variant="caption" color="textSecondary">
                        {t('gap-analysis:assessment.detail.fields.accessExpires')}
                      </Typography>
                      <Typography variant="body1">
                        {formatDateTimeToDateTimeBR(assessment.tokenExpiration)}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </Card>
          </Grid>

          {/* Trust Score Card */}
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('gap-analysis:assessment.detail.sections.trustScore')}
                </Typography>
                <Divider sx={{ mb: 2 }} />

                {trustScoreLoading ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={30} />
                  </Box>
                ) : trustScore ? (
                  <Box>
                    <Box display="flex" justifyContent="center" mb={2}>
                      <Typography variant="h2" fontWeight="bold" color={
                        trustScore.percentage >= 90 ? 'success.main' :
                        trustScore.percentage >= 70 ? 'warning.main' : 'error.main'
                      }>
                        {trustScore.percentage}%
                      </Typography>
                    </Box>
                    <Typography variant="body2" align="center" gutterBottom>
                      {trustScore.classification}
                    </Typography>
                    <Button
                      fullWidth
                      variant="outlined"
                      onClick={() => setShowTrustScore(true)}
                      sx={{ mt: 2 }}
                    >
                      {t('gap-analysis:assessment.detail.trustScore.viewDetails')}
                    </Button>
                  </Box>
                ) : (
                  <Typography variant="body2" color="textSecondary" align="center">
                    {t('gap-analysis:assessment.detail.trustScore.notCalculated')}
                  </Typography>
                )}
              </CardContent>
            </Card>
          </Grid>

          {/* Actions */}
          {assessment.status === 'Under Review' && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('gap-analysis:assessment.detail.sections.reviewActions')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                  <Box display="flex" gap={2}>
                    <Button
                      variant="contained"
                      onClick={() => setShowGapAnalysis(true)}
                    >
                      {t('gap-analysis:assessment.detail.actions.reviewAndUnlock')}
                    </Button>
                    {trustScore && trustScore.percentage < 70 && (
                      <Button
                        variant="outlined"
                        color="warning"
                        onClick={() => setShowExecutiveApproval(true)}
                      >
                        {t('gap-analysis:assessment.detail.actions.requestExecutiveApproval')}
                      </Button>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}

          {/* Activity Timeline */}
          <Grid item xs={12}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  {t('gap-analysis:assessment.detail.sections.activityTimeline')}
                </Typography>
                <Divider sx={{ mb: 2 }} />
                <AssessmentTimeline assessmentId={id} />
              </CardContent>
            </Card>
          </Grid>

          {/* Executive Approval Response */}
          {assessment.status === 'Pending Executive Approval' && (
            <Grid item xs={12}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {t('gap-analysis:assessment.detail.sections.executiveApprovalRequired')}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {approvalHistory && (
                    <Alert severity="info" sx={{ mb: 2 }}>
                      <Typography variant="body2">
                        {t('gap-analysis:assessment.detail.approvalRequested')}{' '}
                        <strong>{formatDateTimeToDateTimeBR(approvalHistory.requestedAt)}</strong>
                        {approvalHistory.notes && (
                          <> — {approvalHistory.notes}</>
                        )}
                      </Typography>
                    </Alert>
                  )}

                  <TextField
                    fullWidth
                    multiline
                    rows={3}
                    label={t('gap-analysis:assessment.detail.justification')}
                    required
                    value={executiveJustification}
                    onChange={(e) => setExecutiveJustification(e.target.value)}
                    placeholder={t('gap-analysis:assessment.detail.justificationPlaceholder')}
                    helperText={t('gap-analysis:assessment.detail.justificationHelper')}
                    sx={{ mb: 2 }}
                  />

                  <Box display="flex" gap={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      color="error"
                      onClick={() => rejectMutation.mutate()}
                      disabled={!executiveJustification.trim() || rejectMutation.isLoading || approveMutation.isLoading}
                    >
                      {rejectMutation.isLoading ? <CircularProgress size={20} /> : t('gap-analysis:assessment.detail.reject')}
                    </Button>
                    <Button
                      variant="contained"
                      color="success"
                      onClick={() => approveMutation.mutate()}
                      disabled={!executiveJustification.trim() || approveMutation.isLoading || rejectMutation.isLoading}
                    >
                      {approveMutation.isLoading ? <CircularProgress size={20} /> : t('gap-analysis:assessment.detail.approve')}
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            </Grid>
          )}
        </Grid>

        {/* Trust Score Detail Dialog */}
        <Dialog
          open={showTrustScore}
          onClose={() => setShowTrustScore(false)}
          maxWidth="md"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t('gap-analysis:assessment.detail.dialogs.trustScoreDetails')}</Typography>
              <IconButton onClick={() => setShowTrustScore(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            {trustScore && <TrustScoreDetail trustScore={trustScore} />}
          </DialogContent>
        </Dialog>

        {/* Gap Analysis Dialog */}
        <Dialog
          open={showGapAnalysis}
          onClose={() => setShowGapAnalysis(false)}
          maxWidth="lg"
          fullWidth
          disableEnforceFocus
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t('gap-analysis:assessment.detail.dialogs.gapAnalysis')}</Typography>
              <IconButton onClick={() => setShowGapAnalysis(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <GapAnalysisView
              assessment={assessment}
              controls={controlResponses}
              trustScore={trustScore}
              onRefresh={() => {
                queryClient.invalidateQueries(['assessment', id]);
                queryClient.invalidateQueries(['control-responses', id]);
                queryClient.invalidateQueries(['trust-score', id]);
              }}
            />
          </DialogContent>
        </Dialog>

        {/* Executive Approval Dialog */}
        <Dialog
          open={showExecutiveApproval}
          onClose={() => setShowExecutiveApproval(false)}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">{t('gap-analysis:assessment.detail.dialogs.executiveApproval')}</Typography>
              <IconButton onClick={() => setShowExecutiveApproval(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
          </DialogTitle>
          <DialogContent>
            <ExecutiveApprovalModal
              assessmentId={assessment._id}
              trustScore={trustScore}
              onClose={() => setShowExecutiveApproval(false)}
              onSuccess={() => {
                setShowExecutiveApproval(false);
                queryClient.invalidateQueries(['assessment', id]);
              }}
            />
          </DialogContent>
        </Dialog>
      </Box>
    </Container>
  );
};
